import React, { useState, useEffect } from 'react';
import apiClient from '../../services/apiClient';
import './ContactSubmissions.css';

const ContactSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.request('/contact/submissions', {
        method: 'GET'
      });
      
      if (response.success) {
        setSubmissions(response.submissions);
      }
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError('Erreur lors du chargement des demandes de contact');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await apiClient.request(`/contact/submissions/${id}`, {
        method: 'PUT'
      });
      // Update local state
      setSubmissions(submissions.map(sub => 
        sub.id === id ? { ...sub, read_status: 1 } : sub
      ));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }

    try {
      await apiClient.request(`/contact/submissions/${id}`, {
        method: 'DELETE'
      });
      // Remove from local state
      setSubmissions(submissions.filter(sub => sub.id !== id));
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(null);
      }
    } catch (err) {
      console.error('Error deleting submission:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = submissions.filter(sub => sub.read_status === 0).length;

  if (loading) {
    return (
      <div className="contact-submissions">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          Chargement des demandes...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contact-submissions">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="contact-submissions">
      <div className="submissions-header">
        <h2>
          <i className="fas fa-inbox"></i>
          Demandes de Contact
        </h2>
        <div className="header-controls">
          <div className="view-toggle">
            <button 
              className={viewMode === 'table' ? 'active' : ''} 
              onClick={() => setViewMode('table')}
              title="Vue tableau"
            >
              <i className="fas fa-table"></i>
            </button>
            <button 
              className={viewMode === 'cards' ? 'active' : ''} 
              onClick={() => setViewMode('cards')}
              title="Vue cartes"
            >
              <i className="fas fa-th-large"></i>
            </button>
          </div>
          <div className="submissions-stats">
            <span className="stat">
              <i className="fas fa-envelope"></i>
              {submissions.length} total
            </span>
            {unreadCount > 0 && (
              <span className="stat unread">
                <i className="fas fa-envelope-open"></i>
                {unreadCount} non lues
              </span>
            )}
          </div>
        </div>
      </div>

      {submissions.length === 0 ? (
        <div className="no-submissions">
          <i className="fas fa-inbox"></i>
          <p>Aucune demande de contact pour le moment</p>
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-view">
          <table className="submissions-table">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Sujet</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map(submission => (
                <tr 
                  key={submission.id} 
                  className={submission.read_status === 0 ? 'unread' : 'read'}
                >
                  <td>
                    {submission.read_status === 0 && <span className="status-dot unread"></span>}
                    {submission.read_status === 1 && <span className="status-dot read"></span>}
                  </td>
                  <td>
                    <strong>{submission.name}</strong>
                  </td>
                  <td>
                    <a href={`mailto:${submission.email}`}>{submission.email}</a>
                  </td>
                  <td>
                    {submission.phone ? (
                      <a href={`tel:${submission.phone}`}>{submission.phone}</a>
                    ) : (
                      <span className="empty">-</span>
                    )}
                  </td>
                  <td>{submission.subject || <span className="empty">-</span>}</td>
                  <td className="date-cell">
                    {formatDate(submission.created_at)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => {
                          setSelectedSubmission(submission);
                          if (submission.read_status === 0) {
                            markAsRead(submission.id);
                          }
                        }}
                        className="view-btn"
                        title="Voir les détails"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      {submission.read_status === 0 && (
                        <button
                          onClick={() => markAsRead(submission.id)}
                          className="check-btn"
                          title="Marquer comme lue"
                        >
                          <i className="fas fa-check"></i>
                        </button>
                      )}
                      <button
                        onClick={() => deleteSubmission(submission.id)}
                        className="trash-btn"
                        title="Supprimer"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="submissions-layout">
          <div className="submissions-list">
            {submissions.map(submission => (
              <div
                key={submission.id}
                className={`submission-item ${submission.read_status === 0 ? 'unread' : ''} ${selectedSubmission?.id === submission.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedSubmission(submission);
                  if (submission.read_status === 0) {
                    markAsRead(submission.id);
                  }
                }}
              >
                <div className="submission-item-header">
                  <span className="submission-name">
                    {submission.read_status === 0 && <i className="fas fa-circle unread-dot"></i>}
                    {submission.name}
                  </span>
                  <span className="submission-date">{formatDate(submission.created_at)}</span>
                </div>
                <div className="submission-item-meta">
                  <span><i className="fas fa-envelope"></i> {submission.email}</span>
                  {submission.phone && <span><i className="fas fa-phone"></i> {submission.phone}</span>}
                </div>
                {submission.training && (
                  <div className="submission-training">
                    <i className="fas fa-graduation-cap"></i> {submission.training}
                  </div>
                )}
              </div>
            ))}
          </div>

          {selectedSubmission && (
            <div className="submission-detail">
              <div className="detail-header">
                <h3>Détails de la demande</h3>
                <button 
                  className="btn-delete"
                  onClick={() => deleteSubmission(selectedSubmission.id)}
                  title="Supprimer"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>

              <div className="detail-content">
                <div className="detail-section">
                  <h4><i className="fas fa-user"></i> Informations personnelles</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Nom complet</label>
                      <p>{selectedSubmission.name}</p>
                    </div>
                    <div className="detail-item">
                      <label>Email</label>
                      <p><a href={`mailto:${selectedSubmission.email}`}>{selectedSubmission.email}</a></p>
                    </div>
                    {selectedSubmission.phone && (
                      <div className="detail-item">
                        <label>Téléphone</label>
                        <p><a href={`tel:${selectedSubmission.phone}`}>{selectedSubmission.phone}</a></p>
                      </div>
                    )}
                    {selectedSubmission.city && (
                      <div className="detail-item">
                        <label>Ville</label>
                        <p>{selectedSubmission.city}</p>
                      </div>
                    )}
                  </div>
                </div>

                {(selectedSubmission.training || selectedSubmission.experience) && (
                  <div className="detail-section">
                    <h4><i className="fas fa-graduation-cap"></i> Formation souhaitée</h4>
                    <div className="detail-grid">
                      {selectedSubmission.training && (
                        <div className="detail-item">
                          <label>Formation d'intérêt</label>
                          <p>{selectedSubmission.training}</p>
                        </div>
                      )}
                      {selectedSubmission.experience && (
                        <div className="detail-item">
                          <label>Expérience</label>
                          <p>{selectedSubmission.experience}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedSubmission.message && (
                  <div className="detail-section">
                    <h4><i className="fas fa-comment-alt"></i> Message</h4>
                    <div className="message-box">
                      <p>{selectedSubmission.message}</p>
                    </div>
                  </div>
                )}

                <div className="detail-section">
                  <h4><i className="fas fa-info-circle"></i> Informations système</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Date de soumission</label>
                      <p>{formatDate(selectedSubmission.created_at)}</p>
                    </div>
                    <div className="detail-item">
                      <label>Statut</label>
                      <p>
                        <span className={`status-badge ${selectedSubmission.read_status === 1 ? 'read' : 'unread'}`}>
                          {selectedSubmission.read_status === 1 ? 'Lu' : 'Non lu'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal for Table View */}
      {viewMode === 'table' && selectedSubmission && (
        <div className="detail-modal" onClick={() => setSelectedSubmission(null)}>
          <div className="detail-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-info-circle"></i> Détails de la demande
              </h3>
              <button 
                className="modal-close" 
                onClick={() => setSelectedSubmission(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h4><i className="fas fa-user"></i> Informations personnelles</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Nom</label>
                    <p>{selectedSubmission.name}</p>
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    <p><a href={`mailto:${selectedSubmission.email}`}>{selectedSubmission.email}</a></p>
                  </div>
                  {selectedSubmission.phone && (
                    <div className="detail-item">
                      <label>Téléphone</label>
                      <p><a href={`tel:${selectedSubmission.phone}`}>{selectedSubmission.phone}</a></p>
                    </div>
                  )}
                  {selectedSubmission.subject && (
                    <div className="detail-item">
                      <label>Sujet</label>
                      <p>{selectedSubmission.subject}</p>
                    </div>
                  )}
                </div>
              </div>

              {selectedSubmission.message && (
                <div className="detail-section">
                  <h4><i className="fas fa-comment-alt"></i> Message</h4>
                  <div className="message-box">
                    <p>{selectedSubmission.message}</p>
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h4><i className="fas fa-info-circle"></i> Informations système</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Date de soumission</label>
                    <p>{formatDate(selectedSubmission.created_at)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Statut</label>
                    <p>
                      <span className={`status-badge ${selectedSubmission.read_status === 1 ? 'read' : 'unread'}`}>
                        {selectedSubmission.read_status === 1 ? 'Lu' : 'Non lu'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                {selectedSubmission.read_status === 0 && (
                  <button
                    onClick={() => {
                      markAsRead(selectedSubmission.id);
                      setSelectedSubmission(null);
                    }}
                    className="mark-read-btn"
                  >
                    <i className="fas fa-check"></i> Marquer comme lue
                  </button>
                )}
                <button
                  onClick={() => {
                    deleteSubmission(selectedSubmission.id);
                    setSelectedSubmission(null);
                  }}
                  className="delete-btn"
                >
                  <i className="fas fa-trash"></i> Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSubmissions;
